/* eslint-disable @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any*/
import React, { ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { ClinicAvatar } from 'shared/components/Avatars';
import { format, isDate, isValid } from 'date-fns';
import partial from 'lodash-es/partial';
import { FormExportMenu } from './FormExport/FormExportMenu';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { FormQuestion, FormQuestionType } from 'shared/types/forms';
import { setTimeValueFromString } from 'shared/utils/setTimeValueFromString';
import { FormSubmissionFragment } from 'shared/types/graphql';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import { Flex } from '@televet/kibble-ui/build/chakra';
import { Link } from '@televet/kibble-ui/build/components/Link';

interface IFormAttachmentData {
  formSubmission: FormSubmissionFragment | undefined;
  formSubmissionLoading: boolean;
  clinicPetParentId: string;
}

function CallDeflectionFormAttachment({ formSubmission, clinicPetParentId }: IFormAttachmentData) {
  const templateTitle = formSubmission?.formTemplate?.title;
  const formContent = formSubmission?.completedContent || formSubmission?.draftContent;
  const parsedFormContent = typeof formContent === 'string' ? JSON.parse(formContent) : formContent;
  const petInfo = useMemo(() => formSubmission?.clinicPet, [formSubmission]);

  const formProp = {
    id: formSubmission?.id || '',
    title: formSubmission?.formTemplate?.title || 'Form',
    content: parsedFormContent as any,
    pet: formSubmission?.clinicPet as any,
  } as any;

  const petParent = formSubmission?.clinicPetParent;
  const petParentProp = {
    id: clinicPetParentId,
    name: petParent ? `${petParent.firstName} ${petParent.lastName}` : '',
  };

  const assignedId = formSubmission?.clinicPet?.id;
  const petsProp =
    formSubmission?.clinicPetParent?.pets?.map((p) => ({
      id: p.id,
      name: p.name,
      pimsId: p.pimsId,
      species: p.speciesType,
      assigned: p.id === assignedId,
    })) || [];
  const [expanded, setExpanded] = useState(false);
  const { viewClinicPetParent } = useSidePanelSearch();
  const handleViewProfileClick = async (clinicPetParentId: string): Promise<void> => {
    viewClinicPetParent({ clinicPetParentId });
  };

  const formQuestionAnswers = parsedFormContent?.questions as FormQuestionAnswer[];

  return (
    <Flex cursor="pointer" align="center" justify="space-between">
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Header>
            <ClinicAvatar isAutomated />
            <HeaderText>
              <CallDeflectionTitle>
                <div>{templateTitle}</div>
              </CallDeflectionTitle>
              <FormTemplateTitle onClick={partial(handleViewProfileClick, clinicPetParentId)}>
                Call Concierge - {petParentProp.name || 'New Client'}
              </FormTemplateTitle>
            </HeaderText>
          </Header>
          <Link onClick={() => setExpanded((isExpanded) => !isExpanded)}>
            {!expanded ? 'Show Preview' : 'Hide Preview'}
          </Link>

          {!!petInfo && (
            <PetAvatar
              name={petInfo.name}
              speciesType={petInfo.speciesType}
              isActive={petInfo.computedIsActive}
              isDeceased={petInfo.computedIsDeceased}
              photoUrl={petInfo.photoUrl}
            />
          )}
          {parsedFormContent && <FormExportMenu form={formProp} pets={petsProp} petParent={petParentProp} />}
        </div>

        {expanded && (
          <>
            <div style={{ padding: 10 }}>
              {formQuestionAnswers
                ?.filter(
                  (question) =>
                    question.type !== FormQuestionType.ratingInput && question.type !== FormQuestionType.textBlock,
                )
                .map((question, index) => (
                  <Row key={index}>
                    <Prompt>{question.prompt}</Prompt>
                    <Answer>{renderFormAnswer(question) || '(No answer provided)'}</Answer>
                  </Row>
                ))}
            </div>
          </>
        )}
      </div>
    </Flex>
  );
}

export default CallDeflectionFormAttachment;
interface FormQuestionAnswer extends FormQuestion {
  answer: string | boolean;
}
const renderFormAnswer = ({ type, answer }: FormQuestionAnswer): ReactNode => {
  switch (type) {
    case FormQuestionType.checkbox:
      return Boolean(answer) ? 'Yes' : 'No';
    case FormQuestionType.multipleSelection:
      if (!answer) return '';
      if (Array.isArray(answer)) {
        return (
          <ul>
            {answer.map((value) => {
              return <li key={value}>{value}</li>;
            })}
          </ul>
        );
      }
      return (
        <ul>
          {(answer as string).split(',').map((value) => (
            <li key={value}>{value}</li>
          ))}
        </ul>
      );
    case FormQuestionType.dateInput:
      const [year, month, day] = ((answer as string) || '').split('-').map((string) => Number(string));

      const date = new Date();
      date.setFullYear(year);
      date.setMonth(month - 1);
      date.setDate(day);
      date.setHours(0, 0, 0, 0);

      if (!isDate(date) || !isValid(date)) return '';

      return format(date || '', "cccc',' PP");
    case FormQuestionType.timeInput:
      const timeDate = setTimeValueFromString(new Date(), answer as string);
      if (!isDate(timeDate) || !isValid(timeDate)) return '';
      return <Time>{format(timeDate || '', 'h:mma')}</Time>;
    default:
      return answer;
  }
};

const Header = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-left: 8px;

  & > * {
    font-weight: 600;
    font-size: 16px;
  }
`;

const CallDeflectionTitle = styled.div`
  display: flex;
  align-items: baseline;
`;

const FormTemplateTitle = styled.div`
  color: var(--chakra-colors-text-brand);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: baseline;
`;

const Time = styled.div`
  text-transform: lowercase;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    margin-top: 24px;
  }
`;

const Prompt = styled.div`
  font-weight: 600;
  color: #73747d;
  margin-bottom: 4px;
`;

const Answer = styled.div`
  white-space: pre-wrap;
  line-height: 1.4;

  ul {
    padding-inline-start: 20px;
  }
`;
